/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, Drawer, Grid, useMediaQuery } from '@material-ui/core';
import { useGlobal, Header, NavLink, Typography } from '@rcg/ui-react';
import { useQuery, useReactiveVar } from '@apollo/client';
import { navigate } from '@gatsbyjs/reach-router';
import { globalizationPath } from '../../utils/globalization/globalizationPath';
import Button from '../UiComponents/Button';
import FlightReview from '../FlightReview';
import ItineraryDetails from '../ItineraryDetails';
import ProgressLineItems from '../ProgressLine';
import AirLoader from '../AirLoader';
import { LAUNCH_DARKLY_FLAGS_AIR } from '../../operations/queries';
import { useFlightProfileQuery } from '../../operations/dataAccess/useFlightProfile';
import useSearchRetry from '../../hooks/useSearchRetry';
import { beginSearch } from '../../utils/searchTelemetry';
import useFlightSearch from '../../operations/dataAccess/useFlightSearch';
import useFlightSearchReturn from '../../operations/dataAccess/useFlightSearchReturn';
import {
  filteredResultsVar,
  bookingProfileVar,
  searchResultsVar,
  selectedDepartureVar,
  shouldSearchReturnResultsVar,
  searchParamsVar,
} from '../../operations/localState/reactiveVariables';

import { clearFilters } from '../../operations/localState/setFilters';
import setSearchResults from '../../operations/localState/setSearchResults';
import { setBookingProfile } from '../../operations/localState/setBookingProfile';
import setSelectedDeparture from '../../operations/localState/setSelectedDeparture';
import setSelectedReturn from '../../operations/localState/setSelectedReturn';
import setHeldReservation from '../../operations/localState/setHeldReservation';
import { setSearchParams } from '../../operations/localState/setSearchParams';
import Theme from '../../utils/theme';
import useStyles from './useStyles';
import Dictionary from '../../utils/dictionary';
import useDictionary from '../../hooks/useDictionary';
import flightSearch from '../../utils/flightSearch';
import getBrand from '../../utils/getBrand';
import { getGuests } from '../../utils/payment';

import ResultsSuccess from '../ResultsSuccess';
import ResultsFail from '../ResultsFail';
import InternalError from '../InternalError';
import FlightSearchForm from '../FlightSearchForm';
import FiltersPanel from '../FiltersPanel';
import InfoTooltip from '../InfoTooltip';
import {
  FLIGHT_SEARCH_TYPE,
  NAVIGATION_PATHS,
  SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS,
  SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS,
  SESSION_STORAGE_CHANGE_FLIGHTS,
  SESSION_STORAGE_AVAILABLE_TRANSFERS,
  CHANGE_FLIGHT_MODE,
  SESSION_STORAGE_SEARCH_BACK,
  SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR,
  SESSION_STORAGE_LAST_SEARCH_PARAMS,
  APPLICATION_NAME,
  ENABLE_ONE_WAY_FLAG,
  SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
  SESSION_STORAGE_FLIGHT_TYPE_CODE,
  SUPPORT_PHONE_NUMBER_LD_FLAG,
  BOOKING_COUNTRY,
} from '../../utils/constants';
import { LDUser } from '../../utils/ldUser';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from '../../operations/sessionStorage/utils';
import {
  trackDepartureResultsView,
  trackReturnResultsView,
} from '../../utils/analytics/searchTracking';
import NotificationBannerComponent from '../NotificationBanner';
import useCommonStyles from '../../utils/useCommonStyles';
import { setSessionTokenFromUrl } from '../../utils/common';

const Results = (props) => {
  const { flightType, reSearch } = props;
  const classes = useStyles();
  const styles = useCommonStyles();
  const {
    data: { lang, market },
    renderContent,
  } = useGlobal();
  const currentBrand = getBrand();

  const getDictionaryValue = useDictionary(Dictionary);
  const urlPath = globalizationPath(market, lang, NAVIGATION_PATHS.base);
  const filteredResults = useReactiveVar(filteredResultsVar);
  const selectedDeparture = useReactiveVar(selectedDepartureVar);
  const searchResults = useReactiveVar(searchResultsVar);
  const shouldSearchReturnResults = useReactiveVar(shouldSearchReturnResultsVar);
  const bookingProfile = useReactiveVar(bookingProfileVar);

  const [flightSearchData, setFlightSearchData] = useState({});
  const [shouldSaveResults, setShouldSaveResults] = useState(false);
  const [flightDates, setFlightDates] = useState(null);
  const [lastSearchDate, setLastSearchDate] = useState(null);
  const [nearestAirportResults, setNearestAirportResults] = useState(null);
  const [canOpenFiltersDrawer, setCanOpenFiltersDrawer] = useState(false);
  // Was a hand-rolled timer armed only for the first search of the page, so a second slow search
  // got nothing. The hook repeats twice, per search.
  const { schedule: scheduleSearchRetry, cancel: cancelSearchRetry } = useSearchRetry();
  const [isChangeFlightFlow, setIsChangeFlightFlow] = useState(
    getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS),
  );
  const [isTransfersAvailable, setIsTransfersAvailable] = useState(null);
  const [trackViewSent, setTrackViewSent] = useState(false);
  const [expandedChange, setExpandedChange] = useState(true);
  const [expandedDeparture, setExpandedDeparture] = useState(true);
  const [showNoResultsError, setShowNoResultsError] = useState(
    getSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR) || false,
  );
  const [arrivalResults, setArrivalResults] = useState();
  const [departureResults, setDepartureResults] = useState();
  const [menuItems, setMenuItems] = useState();

  const handleDrawerToggle = () => {
    setCanOpenFiltersDrawer(!canOpenFiltersDrawer);
  };
  const shouldHideFiltersDrawer = useMediaQuery(Theme.breakpoints.up('lg'));

  if (shouldHideFiltersDrawer && canOpenFiltersDrawer) {
    setCanOpenFiltersDrawer(false);
  }
  const [transfersNotification, setTransfersNotification] = useState(false);
  const [selectedFlightType, setSelectedFlightType] = useState(
    getSessionStorageItem(SESSION_STORAGE_FLIGHT_TYPE_CODE),
  );
  const [errorBannerTitle, setErrorBannerTitle] = useState(
    getDictionaryValue(Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesTitle.key),
  );
  const [errorBannerMessage, setErrorBannerMessage] = useState(
    getDictionaryValue(
      Dictionary.airFlightBookFlightsReservationErrorTechnicalIssuesDescription.key,
    ),
  );
  const [isServerError, setIsServerError] = useState(false);
  const gatewayRefetchDone = useRef(false);

  useEffect(() => {
    if (isChangeFlightFlow && isTransfersAvailable) {
      setTransfersNotification(true);
    }
  }, [isChangeFlightFlow, isTransfersAvailable]);

  const {
    data: bookingProfileQueryData,
    error: flightProfileError,
    loading: flightProfileLoading,
  } = useFlightProfileQuery({
    variables: {
      limit: -1,
    },
    // A cached profile is normally reused as is. The one exception is a profile saved before the
    // disembark gateway was requested: it is fetched again, but only ONCE. skip is what stops the
    // render-time write below (the hook drops `data` while skipped), so it must flip to true after
    // that write even if the response still lacks the gateway, or the write loops on every render.
    skip:
      !!bookingProfile.flightProfile &&
      (gatewayRefetchDone.current ||
        !flightSearch.lacksDisembarkGateway(bookingProfile.flightProfile)),
  });
  // Closing the loader is only half of it: without this the page renders its empty results shell
  // and explains nothing. isServerError is what already paints InternalError below.
  // Not when a profile is already cached: the refetch above is best-effort, and a timeout, a 5xx or
  // an expired token there must not replace a page that works from the profile it already has.
  useEffect(() => {
    if (flightProfileError && !bookingProfile.flightProfile) setIsServerError(true);
  }, [flightProfileError]);

  const bookCountryFromSession = getSessionStorageItem(BOOKING_COUNTRY) || {
    country: 'USA',
    officeID: 'mia',
  };

  const flagsVariables = {
    ldUser: LDUser(),
    language: lang,
    clientApp: APPLICATION_NAME,
    application: APPLICATION_NAME,
    brand: currentBrand?.ldBrand,
    bookingCountry: bookCountryFromSession.country,
  };
  const { data: initialFlags, loading: initialFlagLoading } = useQuery(LAUNCH_DARKLY_FLAGS_AIR, {
    fetchPolicy: 'network-only',
    variables: flagsVariables,
  });

  if (bookingProfileQueryData) {
    gatewayRefetchDone.current = true;
    setSessionTokenFromUrl();
    setBookingProfile(bookingProfileQueryData);
  }

  const [
    executeSearchQuery,
    {
      called: flightsSearchCalled,
      loading: flightsSearchLoading,
      data: flightsSearchData,
      error: flightsSearchError,
      refetch: flightsSearchRefetch,
    },
  ] = useFlightSearch({ fetchPolicy: 'network-only' });

  const [
    executeSearchReturnQuery,
    {
      called: flightsSearchReturnCalled,
      loading: flightsSearchReturnLoading,
      data: flightsSearchReturnData,
      error: flightsSearchReturnError,
    },
  ] = useFlightSearchReturn();

  const clearSearchResults = () => {
    setSearchResults({
      flightFilters: null,
      matchingFlights: [],
    });
    setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
  };

  const submitFlightSearch = (queryParams) => {
    const searchParams = {
      ...queryParams.variables,
    };

    searchParams.guests = [];
    queryParams.variables.guestIDs.map((guestId) =>
      searchParams.guests.push(
        bookingProfile.flightProfile?.guests?.find((guest) => guest.id === guestId),
      ),
    );
    setSearchParams(searchParams);
    const guests = getGuests(bookingProfileVar, searchParamsVar);
    const variables = { ...queryParams.variables, guests };
    clearSearchResults();
    executeSearchQuery({ variables });
    setShouldSaveResults(true);

    setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
    setSelectedDeparture(null);
    setSelectedReturn(null);
    setHeldReservation(null);

    beginSearch();
    scheduleSearchRetry(() => flightsSearchRefetch(queryParams));
    setSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR, null);
    setShowNoResultsError(false);
  };
  const guests = getGuests(bookingProfileVar, searchParamsVar);
  const bookingID = bookingProfileVar()?.flightProfile?.id;
  const variables = {
    correlationID: searchResults?.correlationID,
    hashKey: selectedDeparture?.cabin?.hashKey,
    cabinCode: selectedDeparture?.cabin?.cabinClass?.code,
    mode: isChangeFlightFlow ? CHANGE_FLIGHT_MODE : undefined,
    guests,
    bookingID,
  };
  if (
    flightType === FLIGHT_SEARCH_TYPE.RETURN &&
    shouldSearchReturnResults &&
    selectedDeparture &&
    !shouldSaveResults
  ) {
    const queryParams = {
      variables,
    };
    executeSearchReturnQuery(queryParams);
    setShouldSaveResults(true);
    shouldSearchReturnResultsVar(false);
  }

  const getFlightsBooked = (flightProfile) => {
    return flightProfile?.guests?.some(({ flightGuest }) => !flightGuest?.guestFlights);
  };

  useEffect(() => {
    if (bookingProfile?.flightProfile && !isChangeFlightFlow) {
      const hasSomeGuestsFlights = getFlightsBooked(bookingProfile?.flightProfile);
      if (!hasSomeGuestsFlights) {
        navigate(`${urlPath}${NAVIGATION_PATHS.manage}`);
      }
    }
  }, [bookingProfile]);

  useMemo(() => {
    flightSearch.profileData(
      bookingProfile,
      setNearestAirportResults,
      setFlightDates,
      setFlightSearchData,
    );
  }, [bookingProfile]);

  useEffect(() => {
    if (bookingProfile && initialFlags) {
      const { flightProfile } = bookingProfile || {};
      setArrivalResults(flightProfile?.flightWindows?.arrival);
      setDepartureResults(flightProfile?.flightWindows?.departure);

      const items =
        flightProfile &&
        flightSearch.searchFormMenuItems(
          renderContent,
          flightProfile?.guests,
          initialFlags?.ldFlags[ENABLE_ONE_WAY_FLAG],
        );
      setMenuItems(items);
    }
  }, [bookingProfile, initialFlags]);

  useEffect(() => {
    setIsChangeFlightFlow(getSessionStorageItem(SESSION_STORAGE_CHANGE_FLIGHTS));
    setIsTransfersAvailable(getSessionStorageItem(SESSION_STORAGE_AVAILABLE_TRANSFERS));

    if (getSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR)) {
      setSessionStorageItem(SESSION_STORAGE_LAST_SEARCH_PARAMS, null);
    }

    if (
      getSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY) === '' ||
      getSessionStorageItem(SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY) === undefined
    ) {
      const navigationHistoryLastEntry = document.referrer;
      setSessionStorageItem(
        SESSION_STORAGE_NAVIGATION_HISTORY_LAST_ENTRY,
        navigationHistoryLastEntry,
      );
    }

    return null;
  }, []);

  useEffect(() => {
    if (
      shouldSaveResults &&
      flightsSearchReturnCalled &&
      !flightsSearchReturnLoading &&
      flightsSearchReturnData?.returnFlightsSearch
    ) {
      setShouldSaveResults(false);
      setSearchResults(
        {
          ...flightsSearchReturnData.returnFlightsSearch,
          flightType: FLIGHT_SEARCH_TYPE.RETURN,
        },
        true,
        true,
      );
      setLastSearchDate(new Date());
    }
    // eslint-disable-next-line
  }, [flightsSearchReturnData]);

  useEffect(() => {
    if (searchResults && searchResults.flightType === flightType) {
      if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
        if (!shouldSearchReturnResults) {
          trackReturnResultsView();
        }
      }
    }
  }, [searchResults, flightType, shouldSearchReturnResults]);

  useEffect(() => {
    if (flightType === FLIGHT_SEARCH_TYPE.DEPARTURE)
      if (!trackViewSent) {
        trackDepartureResultsView(flightSearchData.arrivalAirportCode);
        setTrackViewSent(true);
      }
  }, [flightSearchData, flightType]);

  const reSearchVar = new URLSearchParams(window.location.search).get('reSearch') || reSearch;

  useEffect(() => {
    if (reSearchVar === 'true') {
      setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
      setSessionStorageItem(SESSION_STORAGE_LAST_RETURN_FLIGHT_RESULTS, null);
      submitFlightSearch({ variables: searchParamsVar() });
    }
    // eslint-disable-next-line
  }, [reSearchVar]);

  useEffect(() => {
    if (flightsSearchReturnError) {
      setSearchResults({
        flightFilters: null,
        matchingFlights: [],
      });
      setSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS, null);
    }
  }, [flightsSearchReturnError]);

  const handleErrorBannerInfo = ({ title, message }) => {
    setErrorBannerTitle(title);
    setErrorBannerMessage(message);
    setShowNoResultsError(true);
    document.querySelector('.main-container').scrollTo(0, 0);
  };

  const getErrorByCode = (error, errorCode) =>
    error.graphQLErrors.find(({ extensions }) => extensions.code === errorCode);

  const handleFlightSearchErrors = (flightsError) => {
    if (flightsError && Array.isArray(flightsError.graphQLErrors)) {
      if (getErrorByCode(flightsError, 'ACE-FS-1006')) {
        return handleErrorBannerInfo({
          title: getDictionaryValue(Dictionary.airBookFlightNoResultsTitle.key),
          message: `${getDictionaryValue(Dictionary.airNoResultsErrorDescription.key)} ${
            initialFlags?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]
          } ${getDictionaryValue(Dictionary.airFlightSearchErrorAssistance.key)} `,
        });
      }
      if (getErrorByCode(flightsError, 'ACE-FS-1028')) {
        return handleErrorBannerInfo({
          title: getDictionaryValue(Dictionary.airBookFlightNoResultsTitle.key),
          message: `${getDictionaryValue(Dictionary.airFlightSearchErrorDescription.key)} ${
            initialFlags?.ldFlags[SUPPORT_PHONE_NUMBER_LD_FLAG]
          } ${getDictionaryValue(Dictionary.airFlightSearchErrorAssistance.key)} `,
        });
      }
      if (flightsError.graphQLErrors.find(({ message }) => message.startsWith('504'))) {
        return setShowNoResultsError(true);
      }
    }
    return setIsServerError(true);
  };

  useEffect(() => {
    const flightsErrors = flightsSearchReturnError || flightsSearchError;
    if (flightsErrors) {
      handleFlightSearchErrors(flightsErrors);
    }
  }, [flightsSearchReturnError, flightsSearchError]);

  if (flightProfileError || flightsSearchError || flightsSearchReturnError) {
    // flightProfileError alone leaves this undefined — guard before dereferencing.
    const error = flightsSearchError || flightsSearchReturnError;
    if (
      error &&
      Array.isArray(error.graphQLErrors) &&
      (getErrorByCode(error, 'ACE-FS-1006') || getErrorByCode(error, 'ACE-FS-1028'))
    ) {
      cancelSearchRetry();
    }
  }

  if (
    shouldSaveResults &&
    flightsSearchCalled &&
    !flightsSearchLoading &&
    !flightsSearchError &&
    flightsSearchData?.flightsSearch?.correlationID !== searchResults?.correlationID
  ) {
    /* istanbul ignore next */
    cancelSearchRetry();
    setShouldSaveResults(false);
    setSearchResults(
      {
        ...flightsSearchData.flightsSearch,
        flightType: FLIGHT_SEARCH_TYPE.DEPARTURE,
      },
      true,
    );
    setLastSearchDate(new Date());
    if (flightType === FLIGHT_SEARCH_TYPE.RETURN) {
      navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
    }
  }

  if (
    shouldSaveResults &&
    flightsSearchCalled &&
    !flightsSearchLoading &&
    flightsSearchData?.flightsSearch?.correlationID === searchResults?.correlationID &&
    flightType === FLIGHT_SEARCH_TYPE.RETURN
  ) {
    /* istanbul ignore next */
    cancelSearchRetry();
    navigate(`${urlPath}${NAVIGATION_PATHS.resultsDeparture}`);
  }

  const incomingStartDate = flightDates ? flightDates.startDate : '';
  const incomingEndDate = flightDates ? flightDates.endDate : '';
  const container = window !== undefined ? () => window.document.body : undefined;

  const commonDictionary = {
    airFromLabel: renderContent(Dictionary.airFromLabel.key, Dictionary.airFromLabel.defaultValue),
    airFlyingToLabel: renderContent(
      Dictionary.airFlyingToLabel.key,
      Dictionary.airFlyingToLabel.defaultValue,
    ),
    airDatesLabel: renderContent(
      Dictionary.airDatesLabel.key,
      Dictionary.airDatesLabel.defaultValue,
    ),
    airSearchFlightsLabel: renderContent(
      Dictionary.airSearchFlightsLabel.key,
      Dictionary.airSearchFlightsLabel.defaultValue,
    ),
    airNoResultsErrorMessage: renderContent(
      Dictionary.airNoResultsErrorMessage.key,
      Dictionary.airNoResultsErrorMessage.defaultValue,
    ),
    airFromPlaceholder: renderContent(
      Dictionary.airFromPlaceholder.key,
      Dictionary.airFromPlaceholder.defaultValue,
    ),
  };

  const renderFlightResult = () => {
    if (flightsSearchLoading || flightsSearchReturnLoading) return null;
    return filteredResults?.length > 0 ? (
      <ResultsSuccess
        handleDrawerToggle={handleDrawerToggle}
        lastSearchDate={lastSearchDate}
        flightType={flightType}
      />
    ) : (
      <>
        {getSessionStorageItem(SESSION_STORAGE_LAST_DEPARTURE_FLIGHT_RESULTS)?.matchingFlights && (
          <ResultsFail className={classes.mobileNoResults} />
        )}
        {!flightsSearchError && (
          <Button
            variant="contained"
            color="primary"
            className={classes.mobileClearFilterButton}
            onClick={clearFilters}>
            {renderContent(
              Dictionary.airClearAllFiltersLabel.key,
              Dictionary.airClearAllFiltersLabel.defaultValue,
            )}
          </Button>
        )}
      </>
    );
  };

  function getFlightTitle(isChangeFlight, typeFlight) {
    if (isChangeFlight) {
      if (typeFlight === FLIGHT_SEARCH_TYPE.DEPARTURE) {
        return getDictionaryValue(Dictionary.airChangeFlightTitleSelectNewDeparting.key);
      }
      if (typeFlight === FLIGHT_SEARCH_TYPE.RETURN) {
        return getDictionaryValue('airChangeReturnFlightTitle');
      }
    } else if (typeFlight === FLIGHT_SEARCH_TYPE.RETURN) {
      return getDictionaryValue(Dictionary.airReturnFlightTitle.key);
    }
    return getDictionaryValue(Dictionary.airSelectFlightTitle.key);
  }
  const back = getSessionStorageItem(SESSION_STORAGE_SEARCH_BACK);
  const renderFlightSearch = Boolean(
    departureResults && menuItems && bookingProfile?.airports && arrivalResults,
  );

  const loaderStatusTxt =
    (flightsSearchLoading ||
      flightsSearchReturnLoading ||
      flightProfileLoading ||
      initialFlagLoading) &&
    getDictionaryValue(Dictionary.loaderSearchFlights.key);

  const isLoading =
    (flightsSearchCalled && flightsSearchLoading) ||
    (flightsSearchReturnCalled && flightsSearchReturnLoading) ||
    // Absence of a profile is not the same as still fetching one. Gating only on the value left
    // the loader spinning forever whenever the profile never arrived — a deep link whose token
    // Manage cannot resolve answers 200 with null sections, and the spinner had no status text
    // because nothing was loading by then.
    (!bookingProfile.flightProfile && !flightProfileError) ||
    initialFlagLoading;
  return (
    <>
      {/* Nothing to describe when the profile failed — it reads the booking off the reactive var,
          which is empty, and the loader used to hide that forever. */}
      {!isLoading && !isServerError && <ItineraryDetails stretchLayout />}
      <AirLoader open={isLoading} status={loaderStatusTxt} />

      <Grid container spacing={2} className={classes.gridContainer}>
        <Grid item md={12} lg={12} className={classes.gridContainerWrapper}>
          <div className={classes.progressLineContainer}>
            <ProgressLineItems selectedFlightType={selectedFlightType} extended />
          </div>
          {isServerError ? (
            <InternalError />
          ) : (
            <div className={classes.resultsContainer}>
              {showNoResultsError && (
                <div className={classes.customBanner}>
                  <div className={styles.notification}>
                    <NotificationBannerComponent
                      title={errorBannerTitle}
                      description={errorBannerMessage}
                      type="error"
                      renderCloseBtn
                      isBannerOpen
                      onClose={() => {
                        setShowNoResultsError(false);
                        setSessionStorageItem(SESSION_STORAGE_NO_FLIGHTS_RESULTS_ERROR, null);
                      }}
                    />
                  </div>
                </div>
              )}

              {isChangeFlightFlow && (
                <div className="change-flight-container">
                  {transfersNotification && (
                    <div className={classes.customBanner}>
                      <div className={styles.notification}>
                        <NotificationBannerComponent
                          title={getDictionaryValue(
                            Dictionary.airFlightsChangeTransferAlertTitle.key,
                          )}
                          description={getDictionaryValue(
                            Dictionary.airFlightsChangeTransferAlertDescription.key,
                          )}
                          type="warning"
                          renderCloseBtn
                          isBannerOpen
                          onClose={() => setTransfersNotification(false)}
                        />
                      </div>
                    </div>
                  )}
                  <FlightReview
                    title={getDictionaryValue(
                      Dictionary.airCurrentFlightInformationChangeFlightTitle.key,
                    )}
                    isChangeFlights={isChangeFlightFlow}
                    isSummary
                    isDesktop={expandedChange}
                    setExpandedChange={setExpandedChange}
                    expandedChange={expandedChange}
                    cardTitle={getDictionaryValue(
                      Dictionary.airCurrentFlightChangeFlightDropdownTitle.key,
                    )}
                    isChange
                  />
                </div>
              )}
              {selectedDeparture && flightType === 'return' && !back && (
                <FlightReview
                  isNewDeparting
                  isSummary
                  isChangeFlights={isChangeFlightFlow}
                  isDesktop={expandedDeparture}
                  expandedChange={expandedChange}
                  setExpandedChange={setExpandedChange}
                  handleAccordion={() => setExpandedDeparture(!expandedDeparture)}
                  cardTitle={
                    isChangeFlightFlow
                      ? getDictionaryValue(Dictionary.airNewDepartureFlightLabel.key)
                      : getDictionaryValue(Dictionary.airDepartingFlightLabel.key)
                  }
                />
              )}
              <div
                className={
                  isChangeFlightFlow || (selectedDeparture && flightType === 'return')
                    ? `${classes.newDepartingSection} flight-results-secoundary-section`
                    : classes.newDepartingSection
                }>
                <Typography
                  variant="h2"
                  className={isChangeFlightFlow ? 'title changeFlight' : 'title'}>
                  {getFlightTitle(isChangeFlightFlow, flightType)}
                </Typography>
                <InfoTooltip
                  description={renderContent(
                    Dictionary.airSelectFlightDescription.key,
                    Dictionary.airSelectFlightDescription.defaultValue,
                  )}
                />
              </div>

              {!isChangeFlightFlow && (
                <p className={classes.flightSubtitle}>
                  {renderContent(
                    Dictionary.airSelectFlightSubtitle.key,
                    Dictionary.airSelectFlightSubtitle.defaultValue,
                  )}
                </p>
              )}

              <div className={classes.resultsWrapper}>
                <Grid item lg={4} xl={3}>
                  <Card elevation={1} className={classes.filterSection}>
                    <FiltersPanel />
                  </Card>
                </Grid>
                {canOpenFiltersDrawer && (
                  <Header
                    position="fixed"
                    elevation={0}
                    className={`${classes.filterPanelHeader} ${
                      currentBrand.id === 'cel' ? 'cel' : ''
                    }`}>
                    <NavLink component="a" onClick={handleDrawerToggle}>
                      <Typography variant="body2" className="headerTypography">
                        {renderContent(
                          Dictionary.airBackLabel.key,
                          Dictionary.airBackLabel.defaultValue,
                        )}
                      </Typography>
                    </NavLink>
                  </Header>
                )}
                <Drawer
                  container={container}
                  variant="temporary"
                  anchor="left"
                  open={canOpenFiltersDrawer}
                  onClose={handleDrawerToggle}
                  classes={{
                    paper: classes.filtersPanelDrawer,
                  }}
                  ModalProps={{
                    keepMounted: true,
                  }}>
                  <FiltersPanel />
                </Drawer>
                <Grid
                  item
                  xs={12}
                  lg={8}
                  xl={9}
                  className={filteredResults?.length === 0 ? classes.centered : ''}>
                  {flightType !== FLIGHT_SEARCH_TYPE.RETURN && (
                    <>
                      <div className={classes.containerFormSearchFlight}>
                        <Card elevation={3} className={classes.searchWrapper}>
                          <CardContent className={classes.searchWrapperCardContent}>
                            {renderFlightSearch && (
                              <FlightSearchForm
                                className={classes.searchFlightsPageForm}
                                submitFlightSearch={submitFlightSearch}
                                arrivalAirportCode={flightSearchData.arrivalAirportCode}
                                bookingID={flightSearchData.bookingID}
                                currency={flightSearchData.currency}
                                incomingStartDate={incomingStartDate}
                                incomingEndDate={incomingEndDate}
                                flightToDefault={nearestAirportResults}
                                minAvailableDate={arrivalResults?.earliestDate}
                                maxAvailableDate={departureResults?.latestDate}
                                acOptions={bookingProfile.airports.nodes}
                                menuItems={menuItems}
                                buttonInLine={false}
                                labelFrom={commonDictionary.airFromLabel}
                                labelTo={commonDictionary.airFlyingToLabel}
                                labelDatepicker={commonDictionary.airDatesLabel}
                                labelButton={commonDictionary.airSearchFlightsLabel}
                                noResultsErrorMessage={commonDictionary.airNoResultsErrorMessage}
                                placeholderFrom={commonDictionary.airFromPlaceholder}
                                setSelectedFlightType={setSelectedFlightType}
                                clearSearchResults={clearSearchResults}
                              />
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    </>
                  )}
                  {renderFlightResult()}
                </Grid>
              </div>
            </div>
          )}
        </Grid>
      </Grid>
    </>
  );
};
export default Results;
